import { useState, useEffect } from "react";
import { ISelectedPlan, IAppointmentTypes, IPropertySelected } from "../interface";
import { FaRedoAlt } from "react-icons/fa";
import { RiFileTextLine } from "react-icons/ri";
import TooltippedElement from "components/TooltippedElement";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface IProps {
  data: ISelectedPlan[];
  selectedProperties: IPropertySelected[];
  setSelectedProperties: React.Dispatch<React.SetStateAction<IPropertySelected[]>>;
  selectedPlan: ISelectedPlan | null;
  setSelectedPlan: React.Dispatch<React.SetStateAction<ISelectedPlan | null>>;
  active: boolean;
}

const ServicePlanContainer = ({ 
  data, 
  selectedProperties, 
  setSelectedProperties,
  selectedPlan,
  setSelectedPlan, 
  active 
}: IProps) => {
  const { t } = useTranslation();
  const tBase = "views.service-plan-dashboard.components.ServicePlanContainer";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const [propertyIds, setPropertyIds] = useState<number[]>([]);

  useEffect(() => {
    if (!selectedProperties) return;
    const propertyIds = selectedProperties.map((property) => Number(property.id));
    setPropertyIds(propertyIds);
  }, [active]);

  // Using post to minify query size and avoid URL length issues
  useQuery(
    ["check-existing-plans", selectedPlan, propertyIds],
    async () => 
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/service-plans/check-existing-plans`,
        {
          servicePlanId: selectedPlan?.id,
          propertyIds: propertyIds,
        },
      ).then(res => {
        const existingIds = res.data ?? []; 
        const updatedProperties = selectedProperties.map((property) => ({
          ...property,
          conflict: existingIds.includes(property.id),
        }));
        if (setSelectedProperties) setSelectedProperties(updatedProperties);
        return res.data;
      }),
    {
      enabled: active && !!selectedPlan && propertyIds.length > 0,
    }
  );

  const details = (title: string, content: any) => {
    return (
      <div className="border-r-2 pr-2">
        <h4 className="font-medium mb-2 text-secondary-dark">{title}</h4>
        <p className="text-center text-sm text-gray-600">{content || "- -"}</p>
      </div>
    )
  }

  return (
    <div className="">
      <div className="text-2xl font-semibold mx-4 border-b">{tr("Service Plans")}</div>
      <div className="p-4 space-y-6">
        {data?.map((plan) => (
          <div key={plan.id} className="border rounded-lg shadow-sm bg-white p-4">
            {/* Plan Name + Select */}
            <div className="flex justify-between items-center mb-2 border-b">
              <div>
                <div className="text-[12px] text-gray-400">{tr("Plan Id")}: {plan.id}</div>
                <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>

              </div>

              <div className="flex items-center space-x-2">
                <div className="mx-auto flex flex-col items-center justify-center">
                  <input 
                    type="radio" 
                    name="service-plan" 
                    id={plan.id}
                    checked={selectedPlan?.id === plan.id}
                    className="w-6 h-6" 
                    onChange={() => {
                      setSelectedPlan(plan);
                    }}
                  />
                  <span className="text-xs text-gray-500 select">{tr("Select")}</span>
                </div>
              </div>  

            </div>
            
            {/* Plan Details */}
            <div className="flex items-center justify-between space-x-2 mb-2">
              <div className="flex items-center space-x-2">
                {details(tr("Duration"), `${plan.duration} ${tr("months")}`)}
                {details(tr("Start Date"), (
                  plan.start_date_type === "new_year" 
                    ? tr("January 1st") 
                    : plan.start_date_type === "immediately" 
                    ? tr("Immediately") 
                    : plan.start_date_type)
                )}
                {details(tr("Expiry"), `${plan.expiring_soon || 0} ${tr("days")}`)}
              </div>
              <div className="flex items-center space-x-2">
                {plan.auto_renew && (
                  <TooltippedElement 
                    message={tr("Auto Renew")}
                    placement="top"
                    element={ <FaRedoAlt size={20} className="text-blue-500" /> }
                  />
                )}
                {plan.is_contract && (
                  <TooltippedElement 
                    message={tr("Contract")}
                    placement="top"
                    element={ <RiFileTextLine size={20} className="text-primary" /> }
                  />
                )}
              </div>
            </div>

            {/* Description */}
            <div className="border-b pb-1 mt-4 font-medium">{tr("Description")}</div>
            <p className="text-sm text-gray-600 mb-4 mt-1">
              {plan.description || "- -"}
            </p>

            {/* Appointment Types */}
            <div>
              <h4 className="font-medium mb-2">
                {tr("Appointment Types")}{" "}
                <span>({plan.appointment_types.length})</span>
              </h4>
              <div className="grid grid-cols-3 gap-3">
                {plan.appointment_types.map((type: IAppointmentTypes) => (
                  <div
                    key={type.id}
                    className="flex justify-between items-center p-2 border rounded bg-gray-50"
                  >
                    <span>{type.label}</span>
                    <span className="text-sm bg-gray-200 rounded-full px-2 py-1">
                      {type.service_plans_toapptypes.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicePlanContainer;
